"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function OurHistoryAndMastery() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222255" id="our-ottawa-story" className="relative w-full py-24 md:py-32 lg:py-40 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-10 left-4 md:left-20 text-[15rem] md:text-[25rem] lg:text-[35rem] font-default font-bold text-primary/5 select-none pointer-events-none leading-none tracking-tighter" aria-hidden="true">
        613
      </div>

      <div className="container mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8 lg:gap-12 items-center">
          
          {}
          <div ref={imageRef} className={`md:col-span-5 transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-muted shadow-xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_ottawa_truck_landmark.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_ottawa_truck_landmark.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/junkr_ottawa_truck_landmark.png-wvc-sizes" alt="JUNKR truck parked near Ottawa landmark" loading="lazy" className="object-cover w-full h-full transition-transform duration-1000 hover:scale-105" />
            </div>
          </div>

          {}
          <div ref={textRef} className={`md:col-span-6 md:col-start-7 flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-4xl md:text-[48px] font-default font-bold text-primary mb-6 tracking-tighter">
              Locally Owned. Fully Insured. Proud to Serve Ottawa.
            </h2>
            <p className="text-xl md:text-2xl font-default font-semibold text-primary/90 mb-8 leading-tight">
              Rooted in integrity and eco-friendly values. Serving Greater Ottawa for over 10 years.
            </p>
            
            <div className="space-y-8 text-base md:text-lg font-default leading-relaxed text-foreground/80 font-normal">
              <p>
                JUNKR isn't just another franchise with a call center in a different time zone. We are a locally owned and operated family business, born and raised right here in the Ottawa Valley. When you call us, you're talking to your neighbors, not a corporate script.
              </p>
              <p>
                Our mission is simple: to provide the most professional, transparent, and eco-friendly junk removal service in the capital. From the suburban streets of Kanata and Barrhaven to the historic heart of the ByWard Market, we know these roads because we live on them.
              </p>
              <p>
                We believe in hard work, fair pricing, and keeping our city clean. By choosing JUNKR, you're supporting a local team that cares about the Ottawa community and ensures that your unwanted items are disposed of responsibly or donated to local charities whenever possible.
              </p>
            </div>

            <div className="mt-12">
              <Button variant="secondary" className="rounded-lg px-8 py-6 font-default font-semibold tracking-wide uppercase text-sm shadow-md">
                Meet the Local Team
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}